import { Eye } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { FileAttachment } from "@/components/shared/FileAttachment";
import { formatBRL } from "./prosthesis-meta";
import type { ServiceDraft } from "./prosthesis-service-draft";
import { valorDoServicoEmTexto } from "@/components/prosthesis/valor-do-pedido";

/** Botão que abre um popup com o detalhamento completo de um serviço do pedido. */
export function ServiceSummaryPopover({ service }: { service: ServiceDraft }) {
  const optionalRows = (
    [
      ["Material", service.materialOptionLabel],
      ["Cor", service.shadeOptionLabel],
      ["Translucidez", service.translucencyOptionLabel],
      ["Tamanho do bloco/disco", service.blockSizeLabel],
      ["Processo de fabricação", service.manufacturingProcessLabel],
      ["Caracterização", service.characterizationOptionLabel],
      ["Acabamento", service.finishingOptionLabel],
    ] as const
  ).filter(([, value]) => value);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-zinc-400 transition-colors hover:text-brand-600 dark:hover:text-primary"
          aria-label="Ver detalhes do serviço"
        >
          <Eye className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 border-zinc-200 bg-white p-4 shadow-lg dark:border-border dark:bg-card"
      >
        <p className="mb-2.5 text-sm font-semibold text-zinc-900 dark:text-foreground">
          {service.serviceCategoryLabel}
        </p>

        {optionalRows.length > 0 ? (
          <div className="space-y-1.5 border-b border-zinc-100 pb-2.5 text-[12.5px] leading-snug dark:border-border">
            {optionalRows.map(([label, value]) => (
              <div key={label} className="flex items-baseline justify-between gap-3">
                <span className="text-zinc-500 dark:text-muted-foreground">{label}</span>
                <span className="text-right font-medium text-zinc-800 dark:text-foreground">{value}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="border-b border-zinc-100 pb-2.5 text-[12px] text-zinc-500 dark:border-border dark:text-muted-foreground">
            Nenhum material/acabamento definido.
          </p>
        )}

        <div className="mt-2.5 space-y-1.5 text-[12.5px] leading-snug">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-zinc-500 dark:text-muted-foreground">Dentes/região</span>
            <span className="text-right font-medium text-zinc-800 dark:text-foreground">
              {service.teeth || "—"}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-zinc-500 dark:text-muted-foreground">Valor</span>
            <span className="text-right font-medium tabular-nums text-zinc-800 dark:text-foreground">
              {service.priceCents == null ? formatBRL(null) : valorDoServicoEmTexto(service)}
            </span>
          </div>
          {service.notes && (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-zinc-500 dark:text-muted-foreground">Observação</span>
              <span className="text-right font-medium text-zinc-800 dark:text-foreground">{service.notes}</span>
            </div>
          )}
          {service.attachmentFileId && service.attachmentFileName && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-500 dark:text-muted-foreground">Arquivo</span>
              <FileAttachment fileId={service.attachmentFileId} fileName={service.attachmentFileName} />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
